"use client";
import { FC, startTransition, useEffect, useState } from "react";
import Image from "next/image";
import { imageLoader } from "@/helpers/imageLoader";
import { isEditing, isPreviewing } from "@builder.io/sdk-react";
import { getDimensions } from "@/helpers/getDimensions";
import { BuilderBlockProps } from "@/types/builder/";

export type ImageBlock = {
  image?: string;
  // received from the server
  intrinsicWidth?: number;
  // received from the server
  intrinsicHeight?: number;

  alt: string;
  priority?: boolean;
  width?: number;
  height?: number;
  upscale?: boolean;
  fit?: "cover" | "bounds" | "crop";
  quality?: 25 | 50 | 75 | 100;
  crop?: (
    | {
        cropStartPosition: "pixels";
        x: number;
        y: number;
      }
    | {
        cropStartPosition: "percentage";
        xOffset: number;
        yOffset: number;
      }
  ) &
    (
      | {
          type: "ratio";
          widthRatio: number;
          heightRatio: number;
        }
      | {
          type: "pixels";
          widthPixels: number;
          heightPixels: number;
        }
    );
  trim?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  orient?: "r" | "l" | "h" | "v" | "hv" | "rv" | "lv";
  blur?: number;
  brightness?: number;
  contrast?: number;
  saturation?: number;
} & BuilderBlockProps;

const DEFAULT_SIZES = "100vw";
const DEFAULT_QUALITY = 50;

const VisualEditorImageBlock: FC<ImageBlock> = (props) => {
  const [intrinsicWidthData, setIntrinsicWidthData] = useState(
    props.intrinsicWidth ?? 0,
  );
  const [intrinsicHeightData, setIntrinsicHeightData] = useState(
    props.intrinsicHeight ?? 0,
  );

  useEffect(() => {
    if (!props.image) {
      return;
    }

    const url = imageLoader(props)({
      src: props.image,
      quality: props.quality ?? DEFAULT_QUALITY,
      width: Number(props.width),
    });
    startTransition(async () => {
      const { intrinsicWidth, intrinsicHeight } = await getDimensions(
        url.toString(),
      );
      startTransition(() => {
        setIntrinsicHeightData(intrinsicHeight);
        setIntrinsicWidthData(intrinsicWidth);
      });
    });
  }, [props]);

  if (!props.image) {
    return (
      <div className="bg-red-300">
        The image source is missing! Please choose an image.
      </div>
    );
  }

  if (!props.alt) {
    return (
      <div className="bg-red-300">
        The alternate text is missing! Please provide a descriptive alt text.
      </div>
    );
  }

  return (
    <Image
      {...props.attributes}
      loader={imageLoader(props)}
      src={props.image}
      width={intrinsicWidthData}
      height={intrinsicHeightData}
      alt={props.alt}
      quality={props.quality ?? DEFAULT_QUALITY}
      fetchPriority={props.priority ? "high" : "auto"}
      loading={props.priority ? "eager" : "lazy"}
      sizes={props.attributes?.sizes ?? DEFAULT_SIZES}
    />
  );
};

export const ImageBlock: FC<ImageBlock> = (props) => {
  if (isEditing() || isPreviewing()) {
    return <VisualEditorImageBlock {...props} />;
  }

  if (!props.image || !props.alt) {
    return null;
  }

  return (
    <Image
      {...props.attributes}
      loader={imageLoader(props)}
      src={props.image}
      width={props.intrinsicWidth}
      height={props.intrinsicHeight}
      alt={props.alt}
      quality={props.quality ?? DEFAULT_QUALITY}
      fetchPriority={props.priority ? "high" : "auto"}
      loading={props.priority ? "eager" : "lazy"}
      sizes={props.attributes?.sizes ?? DEFAULT_SIZES}
    />
  );
};
